import { useApolloClient, useQuery } from "@apollo/client/react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Dialog } from "@/components/common/Dialog";
import { Button } from "@/components/design/Button";
import {
  DocumentContentDocument,
  MyDocumentsDocument,
} from "@/graphql/generated/operations";
import { logApplicationFailure } from "@/lib/user-error";

// Picks existing vault documents for the share-create flow. Picked documents
// are re-uploaded as SharedItem images/files (a deliberate copy for now -
// SharedItem uploads have no reference to vault documents yet).

export interface PickedDocument {
  name: string;
  mimeType: string;
  content: string;
  preview: string;
}

// The list query has no mimeType field, so image filtering goes by file
// extension (same heuristic the Save service page uses for type badges).
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|avif|svg|bmp|heic|heif|tiff?)$/i;

export function DocumentPickerDialog({
  open,
  kind = "file",
  onClose,
  onPick,
}: {
  open: boolean;
  kind?: "image" | "file";
  onClose: () => void;
  onPick: (files: PickedDocument[]) => void;
}) {
  const intl = useIntl();
  const apollo = useApolloClient();
  const { data, loading, error } = useQuery(MyDocumentsDocument, {
    variables: { input: { first: 50 } },
    skip: !open,
  });
  const allDocs = data?.documents?.nodes ?? [];
  const docs =
    kind === "image"
      ? allDocs.filter((d) => IMAGE_EXT.test(d.fileName ?? d.name ?? ""))
      : allDocs;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const unnamedDocument = intl.formatMessage({
    id: "share.docPicker.unnamedDocument",
    defaultMessage: "Unnamed document",
  });

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function close() {
    setSelected(new Set());
    setLoadError(false);
    onClose();
  }

  async function confirm() {
    if (selected.size === 0 || busy) return;
    setBusy(true);
    setLoadError(false);
    try {
      const picked = await Promise.all(
        docs
          .filter((d) => selected.has(d.id))
          .map(async (d) => {
            const { data: contentData } = await apollo.query({
              query: DocumentContentDocument,
              variables: { id: d.id },
              fetchPolicy: "network-only",
            });
            const file = contentData?.document?.content;
            if (!file?.content) return null;
            const mimeType = file.mimeType ?? "application/octet-stream";
            return {
              name: contentData?.document?.fileName?.trim() || d.name?.trim() || unnamedDocument,
              mimeType,
              content: file.content,
              preview: mimeType.startsWith("image/")
                ? `data:${mimeType};base64,${file.content}`
                : "",
            };
          }),
      );
      const files = picked.filter((f): f is PickedDocument => f !== null);
      if (files.length === 0) {
        setLoadError(true);
        return;
      }
      onPick(files);
      close();
    } catch {
      logApplicationFailure("share.document-picker-fetch");
      setLoadError(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="md"
      title={
        <FormattedMessage id="share.docPicker.title" defaultMessage="Add from my documents" />
      }
    >
      <div data-graphql="MyDocuments, DocumentContent">
        <p className="mb-3 text-[12px] text-ink/55">
          <FormattedMessage
            id="share.docPicker.hint"
            defaultMessage="Selected documents are attached to the post as a copy."
          />
        </p>

        {loading ? (
          <div className="py-6 text-center text-[12px] text-ink/45">…</div>
        ) : error ? (
          <div className="py-6 text-center text-[12px] text-red-600">
            <FormattedMessage
              id="share.docPicker.loadFailed"
              defaultMessage="Loading documents failed."
            />
          </div>
        ) : docs.length === 0 ? (
          <div className="py-6 text-center text-[12px] text-ink/45">
            {kind === "image" ? (
              <FormattedMessage
                id="share.docPicker.emptyImages"
                defaultMessage="No images in your documents yet."
              />
            ) : (
              <FormattedMessage
                id="share.docPicker.empty"
                defaultMessage="No documents in your vault yet."
              />
            )}
          </div>
        ) : (
          <div className="max-h-80 space-y-1 overflow-y-auto p-1">
            {docs.map((d) => {
              const checked = selected.has(d.id);
              return (
                <button
                  key={d.id}
                  type="button"
                  aria-pressed={checked}
                  onClick={() => toggle(d.id)}
                  className={`flex w-full items-center gap-3 rounded-md p-2 text-left ring-1 transition ${
                    checked ? "bg-ink/[0.06] ring-accent" : "ring-ink/5 hover:bg-ink/[0.06]"
                  }`}
                >
                  {d.preview?.content ? (
                    <img
                      src={`data:${d.preview.mimeType};base64,${d.preview.content}`}
                      alt=""
                      className="h-10 w-8 shrink-0 rounded-sm object-cover ring-1 ring-ink/10"
                    />
                  ) : (
                    <span className="flex h-10 w-8 shrink-0 items-center justify-center rounded-sm bg-ink/[0.06] text-base ring-1 ring-ink/10">
                      📄
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px]">
                      {d.name?.trim() || d.fileName?.trim() || unnamedDocument}
                    </span>
                    {d.group?.name ? (
                      <span className="block truncate text-[11px] text-ink/45">{d.group.name}</span>
                    ) : null}
                  </span>
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm text-[10px] ring-1 transition ${
                      checked ? "bg-ink text-on-ink ring-ink" : "text-transparent ring-ink/15"
                    }`}
                  >
                    ✓
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {loadError ? (
          <div className="mt-3 text-[12px] text-red-600">
            <FormattedMessage
              id="share.docPicker.loadFailed"
              defaultMessage="Loading documents failed."
            />
          </div>
        ) : null}

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="secondary" size="compact" onClick={close}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            variant="primary"
            size="compact"
            onClick={() => void confirm()}
            disabled={selected.size === 0 || busy}
            className="disabled:opacity-40"
          >
            {busy ? (
              "…"
            ) : (
              <FormattedMessage
                id="share.docPicker.confirm"
                defaultMessage="Add {count}"
                values={{ count: selected.size }}
              />
            )}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
